import { MouseEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { BlockchainVerifiedBanner } from '../../../../components/blockchain-verified-banner'
import { IUnSdg, IScan, IImpact, IImpactConsumerScanItem } from 'src/types'
import {
  BubbleCard,
  BubbleContent,
  BubbleTitle,
  BubbleSubtitle,
} from './fairtrade-impact-bubbles.styled'
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material'
import { AIGeneratedBanner } from '../../../../components/ai-generated-banner'
import { AIVerifiedBanner } from '../../../../components/ai-verified-banner'
import { AccountImpactDetails } from 'src/components/account-impacts/account-impact-details'
import { CustomModalAnimated } from 'src/components/modal-animated'
import { useCustomModal } from 'src/hooks'
import { Close as CloseIcon } from '@mui/icons-material'
import { stripProductHomeSegment } from 'src/utils/route.utils'

interface IProps {
  title: string | number
  subtitle: string
  image: string
  unSdg: Array<IUnSdg>
  id: string
  quantityName: string
  scans: IScan[]
  primaryColor: string
  walletAddress: string
  showWalletAddress: boolean
  showBcInfo: boolean
  accountImage: string
  isImpactsPage: boolean
  aiGenerated: boolean
  aiVerifiedCount: number
  aiVerifiedInfo: IImpactConsumerScanItem[]
  templateId?: string
  bubbleColor: string
  shapeIndex: number
  description: string
  media: IImpact['media']
  mainMedia: IImpact['mainMedia']
  likes: number
  liked: boolean
  disabled?: boolean
}

export const FairtradeImpactBubble = ({
  image,
  title,
  subtitle,
  id,
  quantityName,
  unSdg,
  scans,
  primaryColor,
  walletAddress,
  showWalletAddress,
  showBcInfo,
  accountImage,
  isImpactsPage,
  aiGenerated,
  aiVerifiedCount,
  aiVerifiedInfo,
  bubbleColor,
  shapeIndex,
  description,
  media,
  mainMedia,
  likes,
  liked,
  disabled,
}: IProps) => {
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { open, handleOpen, handleClose } = useCustomModal()

  const handleCardClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (isImpactsPage) {
      return
    }

    event.preventDefault()
    handleOpen()
  }

  const baseProductPath = stripProductHomeSegment(location.pathname)

  return (
    <Box
      id="fairtrade-impact-bubble-component__templates-fairtrade-components-impact-bubbles__box__l88"
      sx={{ position: 'relative' }}
    >
      <BubbleCard
        id="fairtrade-impact-bubble-component__templates-fairtrade-components-impact-bubbles__bubblecard__l89"
        disabled={!isImpactsPage && disabled}
        bubbleColor={bubbleColor}
        shapeIndex={shapeIndex}
        to={`${baseProductPath}/impacts#${id}`}
        onClick={handleCardClick}
        style={{
          cursor: 'pointer',
        }}
      >
        <BubbleContent id="fairtrade-impact-bubble-component__templates-fairtrade-components-impact-bubbles__bubblecontent__l99">
          {(title || quantityName) && (
            <BubbleTitle id="fairtrade-impact-bubble-component__templates-fairtrade-components-impact-bubbles__bubbletitle__l101">
              {title && (
                <span id="fairtrade-impact-bubble-component__templates-fairtrade-components-impact-bubbles__span__l102">
                  {title}
                </span>
              )}
              {quantityName && (
                <span id="fairtrade-impact-bubble-component__templates-fairtrade-components-impact-bubbles__span__l103">
                  {' '}
                  {quantityName}
                </span>
              )}
            </BubbleTitle>
          )}
          {subtitle && (
            <BubbleSubtitle id="fairtrade-impact-bubble-component__templates-fairtrade-components-impact-bubbles__bubblesubtitle__l106">
              {subtitle}
            </BubbleSubtitle>
          )}
        </BubbleContent>
      </BubbleCard>
      {showBcInfo && scans && scans.length > 0 && (
        <BlockchainVerifiedBanner
          size={40}
          scans={scans}
          stroke={primaryColor}
          walletAddress={walletAddress}
          showWalletAddress={showWalletAddress}
          impactImage={image}
          impactName={subtitle}
          accountImage={accountImage}
          sx={{
            top: '25px',
            right: '0px',
          }}
        />
      )}
      {aiGenerated && aiVerifiedCount > 0 && aiVerifiedInfo && (
        <AIVerifiedBanner aiVerifiedCount={aiVerifiedCount} data={aiVerifiedInfo} />
      )}
      {aiGenerated && <AIGeneratedBanner />}
      {!isImpactsPage && !disabled && (
        <CustomModalAnimated
          ariaLable={`modal-fairtrade-account-impact-details-${id}`}
          open={open}
          handleClose={handleClose}
        >
          <Box
            id="fairtrade-impact-bubble-component__templates-fairtrade-components-impact-bubbles__box__l131"
            sx={{
              zIndex: 101,
              width: '100vw',
              height: '100dvh',
              maxHeight: '100dvh',
              backgroundColor: theme.palette.common.white,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <IconButton
              id="fairtrade-impact-bubble-component__templates-fairtrade-components-impact-bubbles__iconbutton__l143"
              onClick={handleClose}
              sx={{
                color: theme.palette.common.black,
                alignSelf: 'flex-end',
                m: isMobile ? '0px' : '5px',
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              id="fairtrade-impact-bubble-component__templates-fairtrade-components-impact-bubbles__box__l153"
              sx={{
                width: '100%',
                height: '100%',
                maxHeight: '100dvh',
                overflowY: 'auto',
              }}
            >
              <AccountImpactDetails
                title={title}
                quantityName={quantityName}
                subtitle={subtitle}
                image={image}
                unSdg={unSdg || []}
                media={media || []}
                mainMedia={mainMedia || []}
                description={description}
                likes={likes}
                liked={liked}
                impactId={id}
                showUnSdg
                aiGenerated={aiGenerated}
              />
            </Box>
          </Box>
        </CustomModalAnimated>
      )}
    </Box>
  )
}
